// /withdraw-history: every payout, newest first, fifty at a time as the list is scrolled to its end.

import type { PageContext } from '../router.ts';
import { cabinetApi } from '../cabinet-api.ts';
import { h } from '../dom.ts';
import { formatCurrency, formatDateTime } from '../format.ts';
import { t } from '../i18n.ts';
import { track } from '../metrics.ts';
import { snackbar } from '../snackbar.ts';

const K = 'influencer.web.withdrawHistory';
const PAGE_SIZE = 50;

/** A payout, as control/withdrawals lists it. */
interface Withdrawal {
    id: string;
    successful?: boolean;
    createdAt: string;
    amount: number;
    currency?: { code?: string } | null;
}

export function withdrawHistory(ctx: PageContext): void {
    const s: { items: Withdrawal[]; page: number; hasMore: boolean; loading: boolean } = { items: [], page: 1, hasMore: true, loading: false };

    ctx.title(`${K}.title`, 'Withdraw History');
    track('influencerWithdrawalViewed');

    async function loadMore(): Promise<void> {
        if (s.loading || !s.hasMore) return;

        s.loading = true;
        ctx.rerender();

        const response = await cabinetApi(
            `control/withdrawals?page=${s.page}&pageSize=${PAGE_SIZE}&sortBy=CreatedAt&sortAscending=false`);

        if (!ctx.active) return;

        if (response.ok) {
            const result = response.json<{ items?: Withdrawal[]; totalCount?: number }>() || {};
            const items: Withdrawal[] = Array.isArray(result.items) ? result.items : [];

            if (items.length) {
                const known = new Set(s.items.map(item => item.id));

                s.items.push(...items.filter(item => !known.has(item.id)));
                s.page++;
            }

            if (items.length < PAGE_SIZE || s.items.length >= (result.totalCount ?? 0)) s.hasMore = false;
        } else if (response.status !== 0) {
            snackbar(`API call failed: ${response.status}`, 'error');
        }

        s.loading = false;
        ctx.rerender();
    }

    // The sentinel at the end of the list loads the next page when it comes into view.
    const observer = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) void ctx.run(loadMore);
    }, { threshold: 1.0 });

    ctx.onDestroy(() => observer.disconnect());

    function view(): HTMLElement {
        const sentinel = h('div', { class: 'loading-container' }, s.loading ? h('div', { class: 'spinner' }) : null);

        observer.disconnect();
        observer.observe(sentinel);

        return h('div', { class: 'page-withdraw' },
            h('div', { class: 'page-content' },
                h('div', { class: 'title' }, t(`${K}.title`)),
                h('div', { class: 'list' },
                    s.items.map(item => h('div', { class: 'item-card' },
                        h('img', { src: item.successful ? '/images/withdrawal_success_icon.svg' : '/images/withdrawal_fail_icon.svg', alt: '' }),
                        h('div', { class: 'card-content' },
                            h('div', { class: 'info' },
                                h('div', { class: 'title' }, t(item.successful ? `${K}.list.card.success.title` : `${K}.list.card.fail.title`)),
                                h('div', { class: 'date' }, formatDateTime(item.createdAt))),
                            h('div', { class: `sum ${item.successful ? '' : 'inactive'}` }, formatCurrency(item.amount, item.currency?.code))))),
                    sentinel)));
    }

    ctx.render(view);
    ctx.run(loadMore);
}
